import localforage from 'localforage';

/**
 * secureStorage — Web
 *
 * Provides AES-GCM 256-bit encryption over IndexedDB (via localforage).
 *
 * Security model:
 *  - A random 256-bit AES-GCM master key is generated on first use.
 *  - The master key is created as NON-EXTRACTABLE and stored as a CryptoKey
 *    object directly in IndexedDB via the Structured Clone Algorithm.
 *    → DevTools shows only opaque metadata (type/algorithm/usages).
 *    → Raw key bytes are NEVER visible in any DevTools panel.
 *    → JavaScript cannot export the raw bytes (crypto.subtle.exportKey throws).
 *  - Every value is encrypted with a unique 96-bit IV before being written to
 *    the data store, so identical plaintexts produce different ciphertexts.
 *  - The combined payload (IV ‖ ciphertext) is stored as a base-64 string.
 *
 * What this protects against:
 *  ✔ Physical disk access / raw IndexedDB file inspection — data is opaque
 *    without the CryptoKey, which itself exposes no raw bytes.
 *  ✔ DevTools IndexedDB inspection — no raw key bytes are stored anywhere;
 *    only an opaque CryptoKey object is persisted.
 *  ✔ Other origins — browser same-origin policy keeps stores isolated.
 *
 * What this does NOT protect against:
 *  ✗ An attacker who can execute arbitrary JS on the same origin (XSS) —
 *    they can reach the in-memory CryptoKey and call encrypt/decrypt within
 *    the browser. For tokens, prefer short-lived tokens and HttpOnly cookies
 *    managed server-side where possible.
 *
 * Migration:
 *  - If an old-format raw base-64 key is found in the legacy 'secure_keys'
 *    store, it is imported, the legacy entry is removed, and the CryptoKey
 *    is stored in the new opaque vault. Existing encrypted data is untouched.
 */

// ─── Encrypted data store (localforage) ──────────────────────────────────────

const dataStore = localforage.createInstance({
  name: 'FleetTechSecure',
  storeName: 'secure_data',
  description: 'AES-GCM encrypted application data',
});

// ─── Legacy key store (read-only, for migration) ─────────────────────────────

const legacyKeyStore = localforage.createInstance({
  name: 'FleetTechSecure',
  storeName: 'secure_keys',
  description: 'Legacy — raw base-64 master key (migrated away from)',
});

const LEGACY_MASTER_KEY_RECORD = '__master_key__';

// ─── Native IDB key vault ─────────────────────────────────────────────────────
//
// We use the native IndexedDB API (not localforage) to store CryptoKey objects.
// The Structured Clone Algorithm used by IDB natively supports CryptoKey —
// the browser persists it as an opaque internal representation; raw bytes are
// never serialised to JSON or exposed in DevTools.

const VAULT_DB_NAME = 'FleetTechSecureKeyVault';
const VAULT_STORE_NAME = 'key_vault';
const VAULT_KEY_RECORD = '__master_key__';

let _vaultDB: IDBDatabase | null = null;

function openVaultDB(): Promise<IDBDatabase> {
  if (_vaultDB) return Promise.resolve(_vaultDB);

  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(VAULT_DB_NAME, 1);

    req.onupgradeneeded = () => {
      req.result.createObjectStore(VAULT_STORE_NAME);
    };

    req.onsuccess = () => {
      _vaultDB = req.result;
      resolve(_vaultDB);
    };

    req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
  });
}

async function vaultGet(id: string): Promise<CryptoKey | null> {
  const db = await openVaultDB();
  return new Promise<CryptoKey | null>((resolve, reject) => {
    const tx = db.transaction(VAULT_STORE_NAME, 'readonly');
    const req = tx.objectStore(VAULT_STORE_NAME).get(id);
    req.onsuccess = () => resolve((req.result as CryptoKey) ?? null);
    req.onerror = () => reject(req.error ?? new Error('IDB get failed'));
  });
}

async function vaultPut(id: string, key: CryptoKey): Promise<void> {
  const db = await openVaultDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(VAULT_STORE_NAME, 'readwrite');
    const req = tx.objectStore(VAULT_STORE_NAME).put(key, id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('IDB put failed'));
  });
}

// ─── In-memory cache ──────────────────────────────────────────────────────────

// Avoids repeated IDB round-trips per encrypt/decrypt operation.
let _cachedKey: CryptoKey | null = null;

// ─── Key management ───────────────────────────────────────────────────────────

async function getMasterKey(): Promise<CryptoKey> {
  if (_cachedKey) return _cachedKey;

  // 1. Check the new opaque vault first.
  const vaultKey = await vaultGet(VAULT_KEY_RECORD);
  if (vaultKey) {
    _cachedKey = vaultKey;
    return _cachedKey;
  }

  // 2. Migration path: check for the legacy plaintext base-64 key.
  const legacyB64 = await legacyKeyStore.getItem<string>(LEGACY_MASTER_KEY_RECORD);
  if (legacyB64) {
    // Import the old raw key bytes so existing encrypted data stays readable.
    const raw = Uint8Array.from(atob(legacyB64), c => c.codePointAt(0) ?? 0);
    const imported = await crypto.subtle.importKey(
      'raw',
      raw,
      { name: 'AES-GCM' },
      false, // re-import as non-extractable — raw bytes no longer accessible
      ['encrypt', 'decrypt'],
    );

    // Persist in the secure vault and erase the legacy plaintext entry.
    await vaultPut(VAULT_KEY_RECORD, imported);
    await legacyKeyStore.removeItem(LEGACY_MASTER_KEY_RECORD);

    _cachedKey = imported;
    return _cachedKey;
  }

  // 3. First-ever run: generate a brand-new non-extractable AES-GCM key.
  const freshKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable — raw bytes are NEVER accessible from JS
    ['encrypt', 'decrypt'],
  );

  // Store the CryptoKey object directly in IDB via Structured Clone.
  // DevTools shows only {type, algorithm, usages} — never raw bytes.
  await vaultPut(VAULT_KEY_RECORD, freshKey);

  _cachedKey = freshKey;
  return _cachedKey;
}

// ─── Crypto helpers ───────────────────────────────────────────────────────────

export async function encryptValue(plaintext: string): Promise<string> {
  const key = await getMasterKey();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
  const encoded = new TextEncoder().encode(plaintext);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded,
  );

  // Pack: IV (12 bytes) ‖ ciphertext → base-64
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return btoa(String.fromCodePoint(...combined));
}

export async function decryptValue(b64: string): Promise<string> {
  const key = await getMasterKey();
  const combined = Uint8Array.from(atob(b64), c => c.codePointAt(0) ?? 0);
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext,
  );
  return new TextDecoder().decode(plaintext);
}

// ─── Public API ───────────────────────────────────────────────────────────────

const secureStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      const encrypted = await dataStore.getItem<string>(key);
      if (!encrypted) return null;
      return await decryptValue(encrypted);
    } catch (error) {
      console.error('[secureStorage] getItem error:', error);
      return null;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    try {
      const encrypted = await encryptValue(value);
      await dataStore.setItem(key, encrypted);
    } catch (error) {
      console.error('[secureStorage] setItem error:', error);
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      await dataStore.removeItem(key);
    } catch (error) {
      console.error('[secureStorage] removeItem error:', error);
    }
  },

  async clear(): Promise<void> {
    try {
      await dataStore.clear();
    } catch (error) {
      console.error('[secureStorage] clear error:', error);
    }
  },
};

export default secureStorage;